import React, { useEffect, useState } from 'react';
import { Plus, Mail, Trash2, Loader2, Users, Minus, Info, AlertCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PageHeader } from '@/components/PageHeader';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth.jsx';

function dollars(cents) {
    if (!cents && cents !== 0) return '—';
    return `$${(cents / 100).toFixed(2)}`;
}

export default function TeamPage() {
    const { user, org } = useAuth();
    const isAdmin = user?.role === 'owner' || user?.role === 'admin';
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [seatInfo, setSeatInfo] = useState(null);
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('member');
    const [loading, setLoading] = useState(true);
    const [inviting, setInviting] = useState(false);
    const [duplicateError, setDuplicateError] = useState(null);

    // Seat add-on dialog state
    const [seatOpen, setSeatOpen] = useState(false);
    const [seatTarget, setSeatTarget] = useState(0);
    const [savingSeats, setSavingSeats] = useState(false);

    const load = async () => {
        try {
            const [m, s] = await Promise.allSettled([
                api.get('/team/members'),
                api.get('/billing/seats'),
            ]);
            if (m.status === 'fulfilled') {
                setMembers(m.value.data.members || []);
                setInvites(m.value.data.pending_invites || []);
            } else {
                toast.error('Failed to load team');
            }
            if (s.status === 'fulfilled') {
                setSeatInfo(s.value.data);
                setSeatTarget(s.value.data.extra_seats || 0);
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const invite = async () => {
        if (!email) { toast.error('Enter an email'); return; }
        setInviting(true);
        setDuplicateError(null);
        try {
            await api.post('/team/invite', { email, role });
            toast.success('Invite sent');
            setEmail('');
            setOpen(false);
            load();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || 'Invite failed';
            if (status === 409) {
                // Show in-dialog actionable error instead of a toast that disappears
                setDuplicateError(detail);
            } else {
                toast.error(detail);
            }
        } finally {
            setInviting(false);
        }
    };

    const remove = async (uid) => {
        try {
            await api.delete(`/team/members/${uid}`);
            toast.success('Member removed');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Remove failed');
        }
    };

    const submitSeats = async () => {
        if (!seatInfo) return;
        setSavingSeats(true);
        try {
            const r = await api.post('/billing/seats', { extra_seats: Number(seatTarget) });
            toast.success(r.data?.note || 'Seats updated.');
            setSeatOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update seats');
        } finally {
            setSavingSeats(false);
        }
    };

    if (loading) return <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

    const totalSeats = (seatInfo?.total_seats ?? ((org?.seats_included || 0) + (org?.extra_seats || 0)));
    const used = members.length + invites.length;
    const supportsAddons = !!seatInfo && (seatInfo.extra_seat_amount_cents || 0) > 0 && (seatInfo.max_extra_seats || 0) > 0;
    const atMaxExtra = seatInfo && (seatInfo.extra_seats || 0) >= (seatInfo.max_extra_seats || 0);

    return (
        <>
            <PageHeader
                title="Team"
                subtitle="Manage seats and invite teammates. Crew/Business plans only."
                testId="team-header"
                actions={
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDuplicateError(null); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="team-invite-open-button" disabled={!isAdmin || used >= totalSeats}>
                                <Plus className="h-4 w-4 mr-1" /> Invite
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display">Invite a teammate</DialogTitle>
                                <DialogDescription>Send a one-time invite link to their email.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div><Label>Email</Label><Input value={email} onChange={(e) => { setEmail(e.target.value); setDuplicateError(null); }} placeholder="teammate@company.com" data-testid="team-invite-email-input" /></div>
                                <div>
                                    <Label>Role</Label>
                                    <Select value={role} onValueChange={setRole}>
                                        <SelectTrigger data-testid="team-invite-role-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="member">Member</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {duplicateError && (
                                    <Alert variant="default" className="border-amber-200 bg-amber-50" data-testid="team-invite-duplicate-error">
                                        <AlertCircle className="h-4 w-4 text-amber-700" />
                                        <AlertTitle className="text-amber-900">Email unavailable</AlertTitle>
                                        <AlertDescription className="text-amber-900 text-sm space-y-2">
                                            <div>{duplicateError}</div>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                <Button size="sm" variant="outline" className="h-7" onClick={() => { navigator.clipboard?.writeText('support@quotesflowairoofing.com'); toast.success('Copied support email'); }} data-testid="team-invite-copy-support">
                                                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy support email
                                                </Button>
                                            </div>
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div>
                            <DialogFooter>
                                <Button onClick={invite} disabled={inviting} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="team-invite-submit-button">
                                    {inviting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />} Send invite
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            <Card className="mb-4">
                <CardContent className="pt-5">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <Users className="h-5 w-5 text-slate-700" />
                            <div>
                                <div className="font-medium text-slate-900" data-testid="team-seats-summary">{used} of {totalSeats} seats used</div>
                                <div className="text-sm text-slate-500">
                                    {seatInfo?.tier ? `${seatInfo.tier} plan` : (org?.tier ? `${org.tier} plan` : 'No active plan')}
                                    {seatInfo?.seats_included != null && (
                                        <> · {seatInfo.seats_included} included
                                            {(seatInfo.extra_seats || 0) > 0 && <>, {seatInfo.extra_seats} extra</>}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {supportsAddons && isAdmin && (
                            <Dialog open={seatOpen} onOpenChange={(v) => { setSeatOpen(v); if (v) setSeatTarget(seatInfo?.extra_seats || 0); }}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" data-testid="team-seats-manage-button">
                                        <Plus className="h-4 w-4 mr-1" /> Manage seats
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle className="font-display">Add or remove seats</DialogTitle>
                                        <DialogDescription>
                                            Extra seats on the <b className="capitalize">{seatInfo.tier}</b> plan cost <b>{dollars(seatInfo.extra_seat_amount_cents)}</b>/{seatInfo.extra_seat_interval} each. Stripe will prorate the change.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-2">
                                        <div className="flex items-center justify-center gap-4">
                                            <Button variant="outline" size="icon" onClick={() => setSeatTarget((n) => Math.max(0, Number(n) - 1))} data-testid="team-seats-decrement" disabled={Number(seatTarget) <= 0}>
                                                <Minus className="h-4 w-4" />
                                            </Button>
                                            <div className="text-3xl font-display font-semibold w-16 text-center" data-testid="team-seats-target-value">{seatTarget}</div>
                                            <Button variant="outline" size="icon" onClick={() => setSeatTarget((n) => Math.min(seatInfo.max_extra_seats, Number(n) + 1))} data-testid="team-seats-increment" disabled={Number(seatTarget) >= seatInfo.max_extra_seats}>
                                                <Plus className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <div className="text-center text-sm text-slate-500">
                                            extra seats (max {seatInfo.max_extra_seats === 999999 ? '∞' : seatInfo.max_extra_seats})
                                        </div>
                                        <Alert>
                                            <Info className="h-4 w-4" />
                                            <AlertTitle>Heads up</AlertTitle>
                                            <AlertDescription>
                                                Your new total will be <b>{(seatInfo.seats_included || 0) + Number(seatTarget)}</b> seats.
                                                Currently using {used}. Charges are prorated to your billing cycle.
                                            </AlertDescription>
                                        </Alert>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setSeatOpen(false)} data-testid="team-seats-cancel">Cancel</Button>
                                        <Button
                                            onClick={submitSeats}
                                            disabled={savingSeats || Number(seatTarget) === Number(seatInfo.extra_seats || 0)}
                                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                            data-testid="team-seats-confirm"
                                        >
                                            {savingSeats ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                                            Confirm change
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>

                    {atMaxExtra && supportsAddons && (
                        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="team-seats-cap-notice">
                            You're at the maximum number of extra seats for this plan. Upgrade your plan to add more.
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="mb-4">
                <CardHeader><CardTitle className="font-display text-lg">Active members</CardTitle></CardHeader>
                <CardContent>
                    {members.length === 0 ? <div className="text-slate-500 text-sm">No members yet.</div> : (
                        <table className="w-full text-sm">
                            <thead><tr className="text-left text-slate-500"><th className="py-2 font-medium">Name</th><th className="font-medium">Email</th><th className="font-medium">Role</th><th></th></tr></thead>
                            <tbody>
                                {members.map((m) => (
                                    <tr key={m.id} className="border-t">
                                        <td className="py-2">{m.full_name}</td>
                                        <td className="text-slate-600">{m.email}</td>
                                        <td><Badge variant="outline" className="capitalize">{m.role}</Badge></td>
                                        <td className="text-right">
                                            {m.role !== 'owner' && isAdmin && (
                                                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(m.id)} data-testid={`team-remove-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle className="font-display text-lg">Pending invites</CardTitle></CardHeader>
                <CardContent>
                    {invites.length === 0 ? <div className="text-slate-500 text-sm">No pending invites.</div> : (
                        <table className="w-full text-sm">
                            <thead><tr className="text-left text-slate-500"><th className="py-2 font-medium">Email</th><th className="font-medium">Role</th><th className="font-medium">Expires</th></tr></thead>
                            <tbody>
                                {invites.map((inv) => (
                                    <tr key={inv.id} className="border-t">
                                        <td className="py-2">{inv.email}</td>
                                        <td><Badge variant="outline" className="capitalize">{inv.role}</Badge></td>
                                        <td className="text-slate-500 font-mono text-xs">{inv.expires_at?.slice(0,10)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
